import axios from 'axios';
import { BACKEND_URL } from '../config_and_helpers/config';
import { logError, logInfo } from '../config_and_helpers/logger';
import { isCanceledRequest } from '../utils/listSearchRequest';

const LOG_CONTEXT = 'storage_client';

/**
 * Lab storage map client — `GET/POST/DELETE /storage-nodes/`.
 *
 * Migration `0217_storage_node` is deployed. `GET` returns a bare array (not a
 * paginated envelope). `POST` `{ parent_id, kind, name, subtype? }` → 201 with
 * the created node. `PATCH /storage-nodes/{id}/` updates a box (`name`, `slot_count`).
 * `DELETE` → 204 and removes descendants. A 405 on PATCH means the server
 * does not store box edits yet.
 *
 * `listStorageNodes` still treats 404 as `notDeployed` so a missing route can
 * fall back to a session-only tree. 403 and 500 are errors, not an empty map.
 *
 * @see project-docs/45_BACKEND_HANDOFF_STORAGE_MAP.md
 */

/**
 * @param {*} errPayload
 * @returns {string[]}
 */
export const formatStorageErrors = errPayload => {
  if (errPayload == null) return ['Request failed. Please try again.'];
  if (typeof errPayload === 'string') return [errPayload];
  if (Array.isArray(errPayload)) return errPayload.map(String);
  if (errPayload.detail) return [String(errPayload.detail)];
  const messages = [];
  Object.entries(errPayload).forEach(([key, value]) => {
    if (value == null) return;
    if (Array.isArray(value)) value.forEach(v => messages.push(`${key}: ${v}`));
    else messages.push(`${key}: ${String(value)}`);
  });
  return messages.length ? messages : ['Request failed. Please try again.'];
};

const fail = (error, op) => {
  const data = error.response?.data ?? error.message;
  const status = error.response?.status;
  logError(LOG_CONTEXT, `${op} failed`, { status: status ?? null, data });
  return {
    success: false,
    status,
    notDeployed: status === 404,
    messages: formatStorageErrors(data),
  };
};

/**
 * GET /storage-nodes/ — the whole lab tree as a flat list.
 *
 * @param {string} token
 * @returns {Promise<{ success: boolean, data?: Array, status?: number, notDeployed?: boolean, messages?: string[] }>}
 */
export const listStorageNodes = async token => {
  try {
    logInfo(LOG_CONTEXT, 'listStorageNodes start');
    const response = await axios.get(`${BACKEND_URL}storage-nodes/`, {
      headers: { Authorization: `Token ${token}` },
    });
    const data = Array.isArray(response.data)
      ? response.data
      : response.data?.results || [];
    logInfo(LOG_CONTEXT, 'listStorageNodes success', { nodeCount: data.length });
    return { success: true, data };
  } catch (error) {
    return fail(error, 'listStorageNodes');
  }
};

/**
 * POST /storage-nodes/ — create one node.
 *
 * @param {string} token
 * @param {{ parent_id: string|number|null, kind: string, name: string, subtype?: string }} payload
 */
export const createStorageNode = async (token, payload) => {
  try {
    logInfo(LOG_CONTEXT, 'createStorageNode start', {
      parentId: payload?.parent_id ?? null,
      kind: payload?.kind,
      subtype: payload?.subtype ?? null,
    });
    const response = await axios.post(`${BACKEND_URL}storage-nodes/`, payload, {
      headers: { Authorization: `Token ${token}` },
    });
    logInfo(LOG_CONTEXT, 'createStorageNode success', { id: response.data?.id });
    return { success: true, data: response.data };
  } catch (error) {
    return fail(error, 'createStorageNode');
  }
};

/**
 * PATCH /storage-nodes/{id}/ — update a box name and slot count.
 * `slot_count: null` clears a slotted box. `methodMissing` is true on 405.
 *
 * @param {string} token
 * @param {string|number} nodeId
 * @param {{ name?: string, slot_count?: number|null, parent_id?: string|number }} payload
 */
export const updateStorageNode = async (token, nodeId, payload) => {
  try {
    logInfo(LOG_CONTEXT, 'updateStorageNode start', {
      nodeId,
      slotCount: payload?.slot_count ?? null,
      parentId: payload?.parent_id ?? null,
    });
    const response = await axios.patch(
      `${BACKEND_URL}storage-nodes/${nodeId}/`,
      payload,
      { headers: { Authorization: `Token ${token}` } }
    );
    logInfo(LOG_CONTEXT, 'updateStorageNode success', {
      id: response.data?.id ?? nodeId,
    });
    return { success: true, data: response.data };
  } catch (error) {
    const result = fail(error, 'updateStorageNode');
    return { ...result, methodMissing: result.status === 405 };
  }
};

/**
 * GET /storage-nodes/{id}/contents/ — lots placed in this box.
 * A 404 means the contents route is not deployed; the chart stays empty.
 *
 * @param {string} token
 * @param {string|number} nodeId
 * @returns {Promise<{ success: boolean, data?: Array, status?: number, notDeployed?: boolean, messages?: string[] }>}
 */
/**
 * GET /storage-nodes/review-lots/ — unplaced lots whose location text the
 * import left for a person. A bare array. 404 means the route is missing.
 *
 * @param {string} token
 * @returns {Promise<{ success: boolean, data?: Array, status?: number, notDeployed?: boolean, messages?: string[] }>}
 */
export const listStorageReviewLots = async token => {
  try {
    logInfo(LOG_CONTEXT, 'listStorageReviewLots start');
    const response = await axios.get(`${BACKEND_URL}storage-nodes/review-lots/`, {
      headers: { Authorization: `Token ${token}` },
    });
    const data = Array.isArray(response.data)
      ? response.data
      : response.data?.results || [];
    logInfo(LOG_CONTEXT, 'listStorageReviewLots success', { lotCount: data.length });
    return { success: true, data };
  } catch (error) {
    return fail(error, 'listStorageReviewLots');
  }
};

/**
 * Lots stored on one shelf, drawer, rack, or box.
 * Pass `signal` so a newer load can abort this one. A cancel is not an empty place.
 *
 * @param {string} token
 * @param {string|number} nodeId
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ success: boolean, data?: Array, cancelled?: boolean, status?: number, notDeployed?: boolean, messages?: string[] }>}
 */
export const listStorageNodeContents = async (token, nodeId, options = {}) => {
  const signal = options?.signal;
  try {
    logInfo(LOG_CONTEXT, 'listStorageNodeContents start', { nodeId });
    const config = { headers: { Authorization: `Token ${token}` } };
    if (signal) config.signal = signal;
    const response = await axios.get(
      `${BACKEND_URL}storage-nodes/${nodeId}/contents/`,
      config
    );
    const data = Array.isArray(response.data)
      ? response.data
      : response.data?.results || [];
    logInfo(LOG_CONTEXT, 'listStorageNodeContents success', {
      nodeId,
      itemCount: data.length,
    });
    return { success: true, data };
  } catch (error) {
    if (isCanceledRequest(error)) {
      logInfo(LOG_CONTEXT, 'listStorageNodeContents cancelled', { nodeId });
      return { success: false, cancelled: true };
    }
    return fail(error, 'listStorageNodeContents');
  }
};

/**
 * DELETE /storage-nodes/{id}/ — removes the node and its descendants server-side.
 *
 * @param {string} token
 * @param {string|number} nodeId
 */
export const deleteStorageNode = async (token, nodeId) => {
  try {
    logInfo(LOG_CONTEXT, 'deleteStorageNode start', { nodeId });
    await axios.delete(`${BACKEND_URL}storage-nodes/${nodeId}/`, {
      headers: { Authorization: `Token ${token}` },
    });
    return { success: true };
  } catch (error) {
    return fail(error, 'deleteStorageNode');
  }
};
